// lib/voxide-sdk/WakeWordListener.ts
//
// Local wake-word detection ("hey acme") built on the browser's Web Speech
// API. Deliberately standalone — no React, no WebSocket, no AudioContext.
//
// Why it matters that this opens nothing: the Voxide backend bills a session
// the moment a WebSocket connects, so detection has to happen entirely in the
// visitor's browser and only hand off to VoxideClient._voiceConnect() once a
// phrase genuinely matched. A listener that fired loosely would quietly burn
// the developer's plan quota.
//
// The Web Speech API was chosen over an on-device WASM keyword spotter
// (Porcupine et al.) because developers type an arbitrary phrase in the
// dashboard and it has to work immediately — WASM spotters need a trained
// model per phrase. The tradeoff, disclosed in the dashboard UI: Chrome/Edge
// only, and Chrome routes audio to Google's speech service while listening.

// Minimal shape of the Web Speech API — TypeScript's DOM lib doesn't ship
// these, and we only touch this much of the surface.
interface SpeechRecognitionAlternativeLike {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  readonly length: number;
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: SpeechRecognitionResultListLike;
}
interface SpeechRecognitionErrorEventLike {
  error: string;
}
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

/** True when this browser can run wake-word detection at all. */
export function isWakeWordSupported(): boolean {
  return getRecognitionCtor() !== null;
}

/** Lowercase, strip punctuation/digits, collapse whitespace. Applied to both
 *  the configured phrase and the live transcript before comparing. */
export function normalizePhrase(text: string): string {
  return (text || "")
    .toLowerCase()
    .replace(/[^\p{L}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = curr;
  }
  return prev[b.length];
}

/**
 * How many character edits we forgive. Speech transcription mishears short
 * brand names constantly ("acme" -> "akme"), but every extra unit of slack
 * also widens what counts as a hit, so this stays deliberately tight and
 * capped: roughly one edit per 8 characters, never more than 2.
 */
function toleranceFor(phrase: string): number {
  return Math.min(2, Math.floor(phrase.length / 8));
}

/**
 * Does `transcript` contain `phrase`, allowing for small mishearings?
 * Exported for testing — the matching rule is the part most worth pinning down,
 * since it decides both false triggers and missed activations.
 */
export function transcriptMatches(transcript: string, phrase: string): boolean {
  const hay = normalizePhrase(transcript);
  const needle = normalizePhrase(phrase);
  if (!hay || !needle) return false;
  if (hay.includes(needle)) return true;

  const tolerance = toleranceFor(needle);
  if (tolerance === 0) return false;

  // Slide a same-word-count window over the transcript rather than diffing the
  // whole thing: "um, hey akme, are you there" should still match "hey acme",
  // but comparing it end-to-end would blow way past the edit budget.
  const words = hay.split(" ");
  const span = needle.split(" ").length;
  for (let i = 0; i + span <= words.length; i++) {
    const window = words.slice(i, i + span).join(" ");
    if (Math.abs(window.length - needle.length) > tolerance) continue;
    if (levenshtein(window, needle) <= tolerance) return true;
  }
  return false;
}

export type WakeWordFailure = "unsupported" | "permission-denied" | "unavailable";

export interface WakeWordListenerOptions {
  phrase: string;
  lang?: string;
  /** Fired when the phrase is heard. The listener has already stopped itself. */
  onDetected: () => void;
  /** Fired when listening can't continue. Permission denial is terminal. */
  onFailure?: (reason: WakeWordFailure) => void;
}

const RESTART_DELAY_MS = 400;
const MAX_CONSECUTIVE_FAILURES = 5;
/** After a match, ignore everything briefly so one utterance can't double-fire
 *  (interim + final results both carry the phrase). */
const COOLDOWN_MS = 3000;

export class WakeWordListener {
  private recognition: SpeechRecognitionLike | null = null;
  private phrase: string;
  private lang: string;
  private onDetected: () => void;
  private onFailure?: (reason: WakeWordFailure) => void;

  /** Whether the caller wants us listening. Distinguishes "the engine stopped
   *  on its own and should restart" from "we were told to stop". */
  private running = false;
  private restartTimer: ReturnType<typeof setTimeout> | null = null;
  private consecutiveFailures = 0;
  private cooldownUntil = 0;
  private destroyed = false;

  constructor(options: WakeWordListenerOptions) {
    this.phrase = options.phrase;
    this.lang = options.lang || "en-US";
    this.onDetected = options.onDetected;
    this.onFailure = options.onFailure;
  }

  get isSupported(): boolean {
    return isWakeWordSupported();
  }

  get isRunning(): boolean {
    return this.running;
  }

  start(): void {
    if (this.destroyed || this.running) return;
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      this.onFailure?.("unsupported");
      return;
    }
    if (!normalizePhrase(this.phrase)) return;

    this.running = true;
    this.consecutiveFailures = 0;
    this.spinUp(Ctor);
  }

  stop(): void {
    this.running = false;
    this.clearRestart();
    this.teardownRecognition();
  }

  destroy(): void {
    this.destroyed = true;
    this.stop();
  }

  private spinUp(Ctor: SpeechRecognitionCtor): void {
    this.teardownRecognition();

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = this.lang;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      if (!this.running || Date.now() < this.cooldownUntil) return;
      // A successful result means the pipeline is healthy — reset the failure
      // budget so an earlier rough patch doesn't eventually stop a working listener.
      this.consecutiveFailures = 0;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result?.[0]?.transcript;
        if (!text) continue;
        if (transcriptMatches(text, this.phrase)) {
          this.cooldownUntil = Date.now() + COOLDOWN_MS;
          // Release the microphone before handing off: the voice session is
          // about to call getUserMedia, and on some platforms an active
          // recognizer holding the mic makes that fail.
          this.stop();
          this.onDetected();
          return;
        }
      }
    };

    recognition.onerror = (event) => {
      const code = event?.error;
      // Permission is terminal — retrying just re-denies forever, and on some
      // browsers re-prompts the visitor in a loop.
      if (code === "not-allowed" || code === "service-not-allowed") {
        this.running = false;
        this.clearRestart();
        this.teardownRecognition();
        this.onFailure?.("permission-denied");
        return;
      }
      // "no-speech" and "aborted" are ordinary in continuous mode (silence, or
      // our own stop()) and shouldn't count against the failure budget.
      if (code !== "no-speech" && code !== "aborted") {
        this.consecutiveFailures++;
      }
    };

    recognition.onend = () => {
      if (!this.running) return; // we stopped it deliberately
      // Chrome ends the stream after a stretch of silence even in continuous
      // mode, so a restart loop is what actually makes this "always listening".
      if (this.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        this.running = false;
        this.onFailure?.("unavailable");
        return;
      }
      this.scheduleRestart(Ctor);
    };

    this.recognition = recognition;
    try {
      recognition.start();
    } catch {
      // start() throws InvalidStateError if the engine is already running;
      // treat it like any other hiccup and let the restart path handle it.
      this.consecutiveFailures++;
      this.scheduleRestart(Ctor);
    }
  }

  private scheduleRestart(Ctor: SpeechRecognitionCtor): void {
    this.clearRestart();
    // Back off as failures accumulate so a browser that's refusing to start
    // isn't hammered several times a second.
    const delay = RESTART_DELAY_MS * Math.max(1, this.consecutiveFailures);
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      if (this.running && !this.destroyed) this.spinUp(Ctor);
    }, delay);
  }

  private clearRestart(): void {
    if (this.restartTimer) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }
  }

  private teardownRecognition(): void {
    const recognition = this.recognition;
    if (!recognition) return;
    this.recognition = null;
    // Detach first: abort() fires onend, and a live handler there would
    // schedule a restart we don't want.
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    try {
      recognition.abort();
    } catch {
      /* already dead */
    }
  }
}
